import React, { useState, useEffect } from 'react'
import { Plus, Edit2, Trash2, Shield, Search, Check } from 'lucide-react'
import toast from 'react-hot-toast'

interface Role {
  id: string
  name: string
  description: string
  permissions: number[]
  permissions_count: number
}

interface Permission {
  id: number
  name: string
  codename: string
  content_type: number
  content_type_name: string
  app_label: string
}

const RoleManagement: React.FC = () => {
  const [roles, setRoles] = useState<Role[]>([])
  const [permissions, setPermissions] = useState<Permission[]>([])
  const [filteredPermissions, setFilteredPermissions] = useState<Permission[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [selectedRole, setSelectedRole] = useState<Role | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedApp, setSelectedApp] = useState<string>('all')
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    permissions: [] as number[]
  })

  useEffect(() => {
    fetchRoles()
    fetchPermissions()
  }, [])

  useEffect(() => {
    filterPermissions()
  }, [searchTerm, selectedApp, permissions])

  const fetchRoles = async () => {
    try {
      setIsLoading(true)
      const response = await fetch('/api/v1/admin/roles/')
      if (response.ok) {
        const data = await response.json()
        setRoles(data.results || data)
      } else {
        toast.error('Failed to load roles')
      }
    } catch (error) {
      console.error('Failed to fetch roles:', error)
      toast.error('Failed to load roles')
    } finally {
      setIsLoading(false)
    }
  }

  const fetchPermissions = async () => {
    try {
      const response = await fetch('/api/v1/admin/permissions/')
      if (response.ok) {
        const data = await response.json()
        setPermissions(data.results || data)
        setFilteredPermissions(data.results || data)
      }
    } catch (error) {
      console.error('Failed to fetch permissions:', error)
    }
  }

  const filterPermissions = () => {
    let filtered = permissions

    if (searchTerm) {
      filtered = filtered.filter(p =>
        p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.codename.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }

    if (selectedApp !== 'all') {
      filtered = filtered.filter(p => p.app_label === selectedApp)
    }

    setFilteredPermissions(filtered)
  }

  const getUniqueApps = () => {
    const apps = [...new Set(permissions.map(p => p.app_label))]
    return apps.sort()
  }

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()

    try {
      const response = await fetch('/api/v1/admin/roles/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      })

      if (response.ok) {
        toast.success('Role created successfully')
        setShowCreateModal(false)
        resetForm()
        fetchRoles()
      } else {
        const error = await response.json()
        toast.error(error.name?.[0] || 'Failed to create role')
      }
    } catch (error) {
      console.error('Failed to create role:', error)
      toast.error('Failed to create role')
    }
  }

  const handleEdit = (role: Role) => {
    setSelectedRole(role)
    setFormData({
      name: role.name,
      description: role.description,
      permissions: role.permissions || []
    })
    setShowEditModal(true)
  }

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedRole) return

    try {
      const response = await fetch(`/api/v1/admin/roles/${selectedRole.id}/`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      })

      if (response.ok) {
        toast.success('Role updated successfully')
        setShowEditModal(false)
        setSelectedRole(null)
        resetForm()
        fetchRoles()
      } else {
        const error = await response.json()
        toast.error(error.name?.[0] || 'Failed to update role')
      }
    } catch (error) {
      console.error('Failed to update role:', error)
      toast.error('Failed to update role')
    }
  }

  const handleDelete = async (role: Role) => {
    if (!confirm(`Are you sure you want to delete the role "${role.name}"?`)) {
      return
    }

    try {
      const response = await fetch(`/api/v1/admin/roles/${role.id}/`, {
        method: 'DELETE'
      })

      if (response.ok) {
        toast.success('Role deleted successfully')
        fetchRoles()
      } else {
        toast.error('Failed to delete role')
      }
    } catch (error) {
      console.error('Failed to delete role:', error)
      toast.error('Failed to delete role')
    }
  }

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      permissions: []
    })
    setSearchTerm('')
    setSelectedApp('all')
  }

  const togglePermission = (permissionId: number) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permissionId)
        ? prev.permissions.filter(id => id !== permissionId)
        : [...prev.permissions, permissionId]
    }))
  }

  const selectAllFiltered = () => {
    const filteredIds = filteredPermissions.map(p => p.id)
    setFormData(prev => ({
      ...prev,
      permissions: [...new Set([...prev.permissions, ...filteredIds])]
    }))
  }

  const deselectAllFiltered = () => {
    const filteredIds = filteredPermissions.map(p => p.id)
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.filter(id => !filteredIds.includes(id))
    }))
  }

  const renderPermissionModal = (isEdit: boolean) => {
    const groupedPermissions: { [key: string]: Permission[] } = {}
    filteredPermissions.forEach(perm => {
      const key = `${perm.app_label}.${perm.content_type_name}`
      if (!groupedPermissions[key]) {
        groupedPermissions[key] = []
      }
      groupedPermissions[key].push(perm)
    })

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] flex flex-col">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-bold">{isEdit ? 'Edit Role' : 'Create New Role'}</h2>
          </div>

          <form onSubmit={isEdit ? handleUpdate : handleCreate} className="flex-1 flex flex-col overflow-hidden">
            <div className="p-6 space-y-4 overflow-y-auto flex-1">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Role Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., Senior Pathologist"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={2}
                  placeholder="Describe this role's responsibilities"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Permissions ({formData.permissions.length} selected)
                  </label>
                  <div className="flex space-x-2">
                    <button
                      type="button"
                      onClick={selectAllFiltered}
                      className="text-xs text-blue-600 hover:text-blue-800"
                    >
                      Select All
                    </button>
                    <button
                      type="button"
                      onClick={deselectAllFiltered}
                      className="text-xs text-gray-600 hover:text-gray-800"
                    >
                      Deselect All
                    </button>
                  </div>
                </div>

                <div className="mb-3 flex space-x-2">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search permissions..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md text-sm"
                    />
                  </div>
                  <select
                    value={selectedApp}
                    onChange={(e) => setSelectedApp(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-md text-sm"
                  >
                    <option value="all">All Apps</option>
                    {getUniqueApps().map(app => (
                      <option key={app} value={app}>{app}</option>
                    ))}
                  </select>
                </div>

                <div className="border border-gray-200 rounded-md max-h-96 overflow-y-auto">
                  {Object.entries(groupedPermissions).map(([group, perms]) => (
                    <div key={group} className="border-b border-gray-100 last:border-0">
                      <div className="bg-gray-50 px-3 py-2 text-sm font-medium text-gray-700 sticky top-0">
                        {group}
                      </div>
                      <div className="p-2 space-y-1">
                        {perms.map(perm => (
                          <label
                            key={perm.id}
                            className="flex items-center space-x-2 px-2 py-1.5 hover:bg-gray-50 rounded cursor-pointer"
                          >
                            <div className="relative flex items-center">
                              <input
                                type="checkbox"
                                checked={formData.permissions.includes(perm.id)}
                                onChange={() => togglePermission(perm.id)}
                                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                              />
                              {formData.permissions.includes(perm.id) && (
                                <Check className="absolute left-0.5 top-0.5 w-3 h-3 text-white pointer-events-none" />
                              )}
                            </div>
                            <span className="text-sm text-gray-700">{perm.name}</span>
                            <span className="text-xs text-gray-400">({perm.codename})</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50">
              <button
                type="button"
                onClick={() => {
                  isEdit ? setShowEditModal(false) : setShowCreateModal(false)
                  setSelectedRole(null)
                  resetForm()
                }}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                {isEdit ? 'Update Role' : 'Create Role'}
              </button>
            </div>
          </form>
        </div>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading roles...</div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Roles & Permissions</h1>
          <p className="text-gray-600 mt-1">Manage user roles and their permissions</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-5 h-5" />
          <span>Create Role</span>
        </button>
      </div>

      {/* Roles Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {roles.map(role => (
          <div key={role.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Shield className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">{role.name}</h3>
                  <p className="text-sm text-gray-500">{role.permissions_count || 0} permissions</p>
                </div>
              </div>
            </div>

            <p className="text-sm text-gray-600 mb-4 line-clamp-2">
              {role.description || 'No description'}
            </p>

            <div className="flex items-center space-x-2 pt-4 border-t border-gray-100">
              <button
                onClick={() => handleEdit(role)}
                className="flex-1 flex items-center justify-center space-x-1 px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
              >
                <Edit2 className="w-4 h-4" />
                <span>Edit</span>
              </button>
              <button
                onClick={() => handleDelete(role)}
                className="flex-1 flex items-center justify-center space-x-1 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-md transition-colors"
              >
                <Trash2 className="w-4 h-4" />
                <span>Delete</span>
              </button>
            </div>
          </div>
        ))}
      </div>

      {roles.length === 0 && (
        <div className="text-center py-12">
          <Shield className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No roles found</h3>
          <p className="text-gray-600 mb-4">Get started by creating your first role</p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-5 h-5" />
            <span>Create Role</span>
          </button>
        </div>
      )}

      {/* Modals */}
      {showCreateModal && renderPermissionModal(false)}
      {showEditModal && selectedRole && renderPermissionModal(true)}
    </div>
  )
}

export default RoleManagement
